import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, ArrowClockwise, ShareNetwork, SignOut } from "@phosphor-icons/react";
import ExtractCard from "@/components/ExtractCard";
import ReminderDialog from "@/components/ReminderDialog";
import InstallHint from "@/components/InstallHint";
import { useAuth } from "@/context/AuthContext";
import { STOIC_STATUE } from "@/lib/theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  const d = iso ? new Date(iso + "T00:00:00") : new Date();
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

export default function DailyForge() {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/daily-extracts`);
      setData(res.data);
    } catch (e) {
      toast.error("Could not load today's forge");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/refresh-extracts`);
      setData(res.data);
      toast.success("A fresh drop has been forged");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Could not forge a new drop");
    } finally {
      setRefreshing(false);
    }
  };

  const handleShareAll = () => {
    const text =
      "5 extracts from life-changing self-help books, every single day.\n\nForge your mind → " +
      window.location.href;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(text)}`,
      "_blank"
    );
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-50">
      {/* Sticky header */}
      <header
        data-testid="daily-header"
        className="sticky top-0 z-40 backdrop-blur-xl bg-stone-950/70 border-b border-stone-800/60"
      >
        <div className="max-w-2xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Flame size={24} weight="fill" className="text-orange-700" />
            <span className="font-mono-ui text-sm tracking-[0.2em] text-stone-200 uppercase">
              The Daily Forge
            </span>
          </div>
          <div className="flex items-center gap-5">
            <ReminderDialog />
            <button
              data-testid="share-app-button"
              onClick={handleShareAll}
              className="flex items-center gap-2 text-stone-400 hover:text-stone-50 transition-colors duration-150"
            >
              <ShareNetwork size={18} weight="bold" />
              <span className="hidden sm:inline text-xs font-mono-ui tracking-widest uppercase">Share</span>
            </button>
            <button
              data-testid="logout-button"
              onClick={logout}
              title="Sign out"
              className="text-stone-400 hover:text-red-500 transition-colors duration-150"
            >
              <SignOut size={18} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-stone-800/60">
        <img
          src={STOIC_STATUE}
          alt=""
          className="absolute right-0 top-0 h-full w-1/2 object-cover object-center opacity-20 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-950 via-stone-950/90 to-transparent" />
        <div className="relative max-w-2xl mx-auto px-5 sm:px-6 py-14 sm:py-20">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono-ui text-[10px] sm:text-xs uppercase tracking-[0.25em] text-orange-600"
          >
            {formatDate(data?.date)}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mt-4 font-serif-display text-4xl sm:text-5xl font-bold leading-[1.05] text-stone-50"
          >
            Five extracts.
            <br />
            <span className="text-stone-400">Forged for the</span>{" "}
            <span className="text-orange-700">relentless.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="mt-5 text-stone-400 text-sm sm:text-base max-w-md leading-relaxed"
          >
            Daily wisdom from life-changing self-help books — stoicism, discipline,
            fitness, wealth and mindset. Read it. Live it. Share it.
          </motion.p>
        </div>
      </section>

      {/* Feed */}
      <main className="max-w-2xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <InstallHint />
        {loading ? (
          <div className="space-y-6" data-testid="loading-state">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-56 bg-stone-900/40 border border-stone-800 rounded-sm animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8" data-testid="extracts-feed">
            {data?.extracts?.map((extract, i) => (
              <ExtractCard key={extract.id} extract={extract} index={i} />
            ))}
          </div>
        )}

        {/* Refresh / new drop */}
        {!loading && (
          <div className="mt-12 flex flex-col items-center gap-4">
            <button
              data-testid="refresh-button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="group flex items-center gap-3 border border-stone-700 hover:border-orange-700 px-6 py-3 rounded-sm transition-colors duration-150 disabled:opacity-50"
            >
              <ArrowClockwise
                size={18}
                weight="bold"
                className={`text-orange-600 ${refreshing ? "animate-spin" : "group-hover:rotate-90 transition-transform duration-300"}`}
              />
              <span className="font-mono-ui text-xs uppercase tracking-[0.2em] text-stone-300 group-hover:text-stone-50">
                {refreshing ? "Forging..." : "Forge a new drop"}
              </span>
            </button>
            <p className="font-mono-ui text-[10px] text-stone-600 tracking-widest uppercase">
              New set every day · Come back tomorrow
            </p>
          </div>
        )}
      </main>

      <footer className="border-t border-stone-800/60 py-8">
        <div className="max-w-2xl mx-auto px-5 sm:px-6 flex items-center justify-between">
          <span className="font-mono-ui text-[10px] tracking-[0.2em] text-stone-600 uppercase">
            The Daily Forge
          </span>
          <span className="font-mono-ui text-[10px] tracking-[0.2em] text-stone-600 uppercase">
            Discipline · Wisdom · Power
          </span>
        </div>
      </footer>
    </div>
  );
}
